import { xchacha20poly1305 } from "@noble/ciphers/chacha.js";
import { x25519 } from "@noble/curves/ed25519.js";
import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";

const DATABASE_NAME = "memory-universe-browser-device";
const DATABASE_VERSION = 1;
const IDENTITY_STORE = "identity";
const MEDIA_STORE = "media";
const OUTBOX_STORE = "relay-outbox";
const RELAY_MAGIC = new TextEncoder().encode("MU-RLY01");
const NONCE_BYTES = 24;
const KEY_BYTES = 32;
const encoder = new TextEncoder();

interface BrowserIdentityRow {
  id: "identity";
  deviceId: string;
  publicKey: ArrayBuffer;
  privateKey: ArrayBuffer;
}

interface BrowserMediaRow {
  mediaId: string;
  fileName: string;
  mimeType: string;
  original: Blob;
  sha256: string;
  byteSize: number;
  createdAt: number;
}

interface BrowserOutboxRow {
  transferId: string;
  ciphertext: Blob;
  ciphertextSha256: string;
  ciphertextSize: number;
}

export interface BrowserDeviceKey {
  deviceId: string;
  keyVersion: number;
  algorithm: "x25519-xchacha20poly1305-v1";
  publicKey: string;
}

export interface BrowserMediaCopy {
  mediaId: string;
  byteSize: number;
  sha256: string;
  storageState: "durable";
}

interface Recipient {
  recipientDeviceId: string;
  recipientKeyId: string;
  publicKey: string;
}

interface Envelope {
  recipientDeviceId: string;
  recipientKeyId: string;
  envelope: string;
}

export interface BrowserPreparedTransfer {
  sourceDeviceId: string;
  sourceSha256: string;
  ciphertextSha256: string;
  ciphertextSize: number;
  algorithm: "x25519-xchacha20poly1305-v1";
  envelopes: Envelope[];
}

export interface BrowserReceivedBinaryTransfer {
  bytes: Uint8Array;
  sha256: string;
}

function openDatabase() {
  if (typeof indexedDB === "undefined" || !globalThis.crypto?.subtle) return Promise.resolve<IDBDatabase | null>(null);
  return new Promise<IDBDatabase | null>((resolve) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(IDENTITY_STORE)) database.createObjectStore(IDENTITY_STORE, { keyPath: "id" });
      if (!database.objectStoreNames.contains(MEDIA_STORE)) database.createObjectStore(MEDIA_STORE, { keyPath: "mediaId" });
      if (!database.objectStoreNames.contains(OUTBOX_STORE)) database.createObjectStore(OUTBOX_STORE, { keyPath: "transferId" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
}

function requestResult<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("浏览器本地存储不可用"));
  });
}

function transactionDone(transaction: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("浏览器本地存储失败"));
    transaction.onabort = () => reject(transaction.error ?? new Error("浏览器本地存储已取消"));
  });
}

function bytesToBase64(bytes: Uint8Array) {
  let output = "";
  for (let index = 0; index < bytes.length; index += 0x8000) output += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  return btoa(output);
}

function base64ToBytes(value: string) {
  const decoded = atob(value);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

function randomBytes(length: number) {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

async function hashBytes(bytes: Uint8Array) {
  const digest = await crypto.subtle.digest("SHA-256", bytes.slice().buffer as ArrayBuffer);
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join("");
}

function envelopeContext(mediaId: string, recipientDeviceId: string) {
  return encoder.encode(`memory-universe-envelope-v1:${mediaId}:${recipientDeviceId}`);
}

function mediaContext(mediaId: string) {
  return encoder.encode(`memory-universe-media-v1:${mediaId}`);
}

function deriveEnvelopeKey(sharedSecret: Uint8Array, mediaId: string, recipientDeviceId: string) {
  return hkdf(sha256, sharedSecret, encoder.encode(mediaId), envelopeContext(mediaId, recipientDeviceId), KEY_BYTES);
}

function sealContentKey(mediaId: string, recipient: Recipient, contentKey: Uint8Array) {
  const recipientPublicKey = base64ToBytes(recipient.publicKey);
  if (recipientPublicKey.length !== KEY_BYTES) throw new Error("目标设备公钥无效");
  const ephemeral = x25519.keygen();
  const nonce = randomBytes(NONCE_BYTES);
  const envelopeKey = deriveEnvelopeKey(x25519.getSharedSecret(ephemeral.secretKey, recipientPublicKey), mediaId, recipient.recipientDeviceId);
  const encrypted = xchacha20poly1305(envelopeKey, nonce, envelopeContext(mediaId, recipient.recipientDeviceId)).encrypt(contentKey);
  const output = new Uint8Array(KEY_BYTES + NONCE_BYTES + encrypted.length);
  output.set(ephemeral.publicKey, 0);
  output.set(nonce, KEY_BYTES);
  output.set(encrypted, KEY_BYTES + NONCE_BYTES);
  return bytesToBase64(output);
}

function openContentKey(mediaId: string, recipientDeviceId: string, privateKey: Uint8Array, envelope: string) {
  const input = base64ToBytes(envelope);
  if (input.length <= KEY_BYTES + NONCE_BYTES) throw new Error("媒体密钥信封无效");
  const envelopeKey = deriveEnvelopeKey(
    x25519.getSharedSecret(privateKey, input.subarray(0, KEY_BYTES)), mediaId, recipientDeviceId
  );
  const contentKey = xchacha20poly1305(
    envelopeKey,
    input.subarray(KEY_BYTES, KEY_BYTES + NONCE_BYTES),
    envelopeContext(mediaId, recipientDeviceId)
  ).decrypt(input.subarray(KEY_BYTES + NONCE_BYTES));
  if (contentKey.length !== KEY_BYTES) throw new Error("媒体密钥信封无效");
  return contentKey;
}

async function readIdentity() {
  const database = await openDatabase();
  if (!database) throw new Error("此浏览器不支持安全本地存储");
  const transaction = database.transaction(IDENTITY_STORE, "readwrite");
  const done = transactionDone(transaction);
  const store = transaction.objectStore(IDENTITY_STORE);
  const current = await requestResult(store.get("identity") as IDBRequest<BrowserIdentityRow | undefined>);
  if (current) {
    await done;
    return current;
  }
  const keyPair = x25519.keygen();
  const identity: BrowserIdentityRow = {
    id: "identity", deviceId: crypto.randomUUID(), publicKey: keyPair.publicKey.slice().buffer, privateKey: keyPair.secretKey.slice().buffer
  };
  store.put(identity);
  await done;
  return identity;
}

async function readMedia(mediaId: string) {
  const database = await openDatabase();
  if (!database) return null;
  const transaction = database.transaction(MEDIA_STORE, "readonly");
  const done = transactionDone(transaction);
  const media = await requestResult(transaction.objectStore(MEDIA_STORE).get(mediaId) as IDBRequest<BrowserMediaRow | undefined>);
  await done;
  return media ?? null;
}

async function saveMedia(row: BrowserMediaRow) {
  const database = await openDatabase();
  if (!database) throw new Error("此浏览器不支持安全本地存储");
  const transaction = database.transaction(MEDIA_STORE, "readwrite");
  transaction.objectStore(MEDIA_STORE).put(row);
  await transactionDone(transaction);
}

async function readOutbox(transferId: string) {
  const database = await openDatabase();
  if (!database) return null;
  const transaction = database.transaction(OUTBOX_STORE, "readonly");
  const done = transactionDone(transaction);
  const row = await requestResult(transaction.objectStore(OUTBOX_STORE).get(transferId) as IDBRequest<BrowserOutboxRow | undefined>);
  await done;
  return row ?? null;
}

async function saveOutbox(row: BrowserOutboxRow) {
  const database = await openDatabase();
  if (!database) throw new Error("此浏览器不支持安全本地存储");
  const transaction = database.transaction(OUTBOX_STORE, "readwrite");
  transaction.objectStore(OUTBOX_STORE).put(row);
  await transactionDone(transaction);
}

export async function browserSecureStorageReady() {
  try { await readIdentity(); return true; } catch { return false; }
}

export async function browserDeviceId() {
  return (await readIdentity()).deviceId;
}

export async function provisionBrowserDeviceEncryptionKey(): Promise<BrowserDeviceKey> {
  const identity = await readIdentity();
  return { deviceId: identity.deviceId, keyVersion: 1, algorithm: "x25519-xchacha20poly1305-v1", publicKey: bytesToBase64(new Uint8Array(identity.publicKey)) };
}

export async function storeBrowserOriginalMedia(mediaId: string, file: File): Promise<BrowserMediaCopy> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (bytes.length === 0) throw new Error("本机原图为空");
  const sha256 = await hashBytes(bytes);
  await saveMedia({ mediaId, fileName: file.name, mimeType: file.type, original: new Blob([bytes], { type: file.type }), sha256, byteSize: bytes.length, createdAt: Date.now() });
  return { mediaId, byteSize: bytes.length, sha256, storageState: "durable" };
}

export async function readBrowserMediaUrl(mediaId: string) {
  const media = await readMedia(mediaId);
  return media ? URL.createObjectURL(media.original) : null;
}

export async function readBrowserMediaOriginalBlob(mediaId: string) {
  const media = await readMedia(mediaId);
  return media?.original ?? null;
}

export async function prepareBrowserEncryptedMediaTransfer(transferId: string, mediaId: string, recipients: Recipient[]): Promise<BrowserPreparedTransfer> {
  const media = await readMedia(mediaId);
  if (!media) throw new Error("本机原图副本缺失");
  const original = new Uint8Array(await media.original.arrayBuffer());
  return prepareBrowserEncryptedBytesTransfer(transferId, mediaId, original, recipients, 20 * 1024 * 1024);
}

export async function prepareBrowserEncryptedBytesTransfer(
  transferId: string,
  contentId: string,
  source: Uint8Array,
  recipients: Recipient[],
  maximumBytes = 1024 * 1024
): Promise<BrowserPreparedTransfer> {
  if (recipients.length === 0 || recipients.length > 32) throw new Error("加密中转目标设备数量无效");
  if (source.length === 0 || source.length > maximumBytes) throw new Error("加密中转资源大小无效");
  const identity = await readIdentity();
  const contentKey = randomBytes(KEY_BYTES);
  const nonce = randomBytes(NONCE_BYTES);
  const encrypted = xchacha20poly1305(contentKey, nonce, mediaContext(contentId)).encrypt(source);
  const ciphertext = new Uint8Array(RELAY_MAGIC.length + NONCE_BYTES + encrypted.length);
  ciphertext.set(RELAY_MAGIC, 0); ciphertext.set(nonce, RELAY_MAGIC.length); ciphertext.set(encrypted, RELAY_MAGIC.length + NONCE_BYTES);
  const ciphertextSha256 = await hashBytes(ciphertext);
  await saveOutbox({ transferId, ciphertext: new Blob([ciphertext], { type: "application/octet-stream" }), ciphertextSha256, ciphertextSize: ciphertext.length });
  return {
    sourceDeviceId: identity.deviceId, sourceSha256: await hashBytes(source), ciphertextSha256, ciphertextSize: ciphertext.length,
    algorithm: "x25519-xchacha20poly1305-v1",
    envelopes: recipients.map((recipient) => ({ recipientDeviceId: recipient.recipientDeviceId, recipientKeyId: recipient.recipientKeyId, envelope: sealContentKey(contentId, recipient, contentKey) }))
  };
}

export async function createBrowserDeferredEncryptedMediaKeyEnvelope(request: { mediaId: string; sourceEnvelope: string; recipient: Recipient }): Promise<Envelope> {
  const identity = await readIdentity();
  const contentKey = openContentKey(request.mediaId, identity.deviceId, new Uint8Array(identity.privateKey), request.sourceEnvelope);
  return { recipientDeviceId: request.recipient.recipientDeviceId, recipientKeyId: request.recipient.recipientKeyId, envelope: sealContentKey(request.mediaId, request.recipient, contentKey) };
}

export async function uploadBrowserPreparedEncryptedMediaTransfer(transferId: string, uploadUrl: string) {
  const row = await readOutbox(transferId);
  if (!row) throw new Error("加密中转待上传副本缺失");
  const response = await fetch(uploadUrl, { method: "PUT", headers: { "content-type": "application/octet-stream", "x-upsert": "false" }, body: row.ciphertext });
  if (!response.ok) throw new Error("加密中转上传失败");
  return { transferId, ciphertextSha256: row.ciphertextSha256, ciphertextSize: row.ciphertextSize };
}

export async function receiveBrowserEncryptedMediaTransfer(request: {
  mediaId: string; recipientDeviceId: string; envelope: string; downloadUrl: string; expectedSourceSha256: string;
  expectedCiphertextSha256: string; expectedCiphertextSize: number; fileName: string; mimeType: string;
}): Promise<BrowserMediaCopy> {
  const binary = await receiveBrowserEncryptedBytesTransfer(request);
  await saveMedia({ mediaId: request.mediaId, fileName: request.fileName, mimeType: request.mimeType, original: new Blob([binary.bytes.slice().buffer], { type: request.mimeType }), sha256: binary.sha256, byteSize: binary.bytes.length, createdAt: Date.now() });
  return { mediaId: request.mediaId, byteSize: binary.bytes.length, sha256: binary.sha256, storageState: "durable" };
}

export async function receiveBrowserEncryptedBytesTransfer(request: {
  mediaId: string; recipientDeviceId: string; envelope: string; downloadUrl: string; expectedSourceSha256: string;
  expectedCiphertextSha256: string; expectedCiphertextSize: number;
}): Promise<BrowserReceivedBinaryTransfer> {
  const identity = await readIdentity();
  if (identity.deviceId !== request.recipientDeviceId) throw new Error("加密中转目标设备不匹配");
  const response = await fetch(request.downloadUrl);
  if (!response.ok) throw new Error("加密中转下载失败");
  const ciphertext = new Uint8Array(await response.arrayBuffer());
  if (ciphertext.length !== request.expectedCiphertextSize || await hashBytes(ciphertext) !== request.expectedCiphertextSha256.toLowerCase()) throw new Error("加密中转媒体校验失败");
  if (!RELAY_MAGIC.every((byte, index) => ciphertext[index] === byte) || ciphertext.length <= RELAY_MAGIC.length + NONCE_BYTES) throw new Error("加密中转媒体格式无效");
  const contentKey = openContentKey(request.mediaId, identity.deviceId, new Uint8Array(identity.privateKey), request.envelope);
  const original = xchacha20poly1305(contentKey, ciphertext.subarray(RELAY_MAGIC.length, RELAY_MAGIC.length + NONCE_BYTES), mediaContext(request.mediaId)).decrypt(ciphertext.subarray(RELAY_MAGIC.length + NONCE_BYTES));
  const sourceSha256 = await hashBytes(original);
  if (original.length === 0 || sourceSha256 !== request.expectedSourceSha256.toLowerCase()) throw new Error("解密后的本机原图校验失败");
  return { bytes: original, sha256: sourceSha256 };
}
